import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { 
  LayoutDashboard, 
  Home, 
  FileText, 
  MessageSquare, 
  Settings, 
  Tag,
  ShieldCheck,
  LogOut,
  Menu,
  X
} from "lucide-react";
import { useState } from "react";

const LOGO_URL = "/logo.png";

export const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const navItems = [
    { href: "/admin", icon: LayoutDashboard, label: "Tableau de bord" },
    { href: "/admin/biens", icon: Home, label: "Biens immobiliers" },
    { href: "/admin/documents", icon: FileText, label: "Documents reçus" },
    { href: "/admin/contacts", icon: MessageSquare, label: "Messages" },
    { href: "/admin/offres", icon: Tag, label: "Offres promotionnelles" },
    { href: "/admin/rgpd", icon: ShieldCheck, label: "Données personnelles" },
    { href: "/admin/parametres", icon: Settings, label: "Paramètres" },
  ];
  
  const isActive = (path) => location.pathname === path;
  
  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };
  
  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      {/* Mobile Header */}
      <div className="lg:hidden bg-[#0A0A0A] text-white p-4 flex items-center justify-between">
        <img src={LOGO_URL} alt="Logo" className="h-10 w-auto bg-white rounded p-1" />
        <button onClick={() => setSidebarOpen(!sidebarOpen)} data-testid="admin-mobile-menu-toggle">
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      
      {/* Sidebar */}
      <aside 
        className={`admin-sidebar transform lg:transform-none transition-transform duration-300 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="p-6 border-b border-gray-800">
          <Link to="/admin">
            <img src={LOGO_URL} alt="MAINGUY IMMOBILIER" className="h-20 w-auto bg-white rounded-lg p-2" />
          </Link>
        </div>
        
        {/* User info */}
        <div className="px-6 py-4 border-b border-gray-800">
          <p className="text-sm text-gray-400">Connecté en tant que</p>
          <p className="font-medium truncate" data-testid="admin-user-name">{user?.name}</p>
        </div>
        
        {/* Navigation */}
        <nav className="py-6">
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className={`admin-nav-item ${isActive(item.href) ? 'active' : ''}`}
              onClick={() => setSidebarOpen(false)}
              data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <item.icon size={20} />
              <span>{item.label}</span>
            </Link>
          ))}
        </nav>
        
        {/* Logout & Back to site */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-800">
          <Link
            to="/"
            className="admin-nav-item text-sm"
            data-testid="admin-back-to-site"
          >
            <Home size={18} />
            <span>Retour au site</span>
          </Link>
          <button
            onClick={handleLogout}
            className="admin-nav-item w-full text-left text-red-400 hover:text-red-300 hover:bg-red-500/10"
            data-testid="admin-logout-button"
          >
            <LogOut size={18} />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>
      
      {/* Main content */}
      <main className="lg:ml-64 min-h-screen">
        <div className="p-6 lg:p-8">
          {children}
        </div>
      </main>
      
      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 lg:hidden z-30"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
